import { useDashboardPermissions } from '@components/utils/DashboardPermissions';
import { SECTION_NAMES } from '@constants/sectionNames';
import { RecommendationButton, RecommendationButtonGroup, Toasts } from '@fourthwall/components';
import { InternalApiClient } from '@fourthwall/services/dist/InternalApiClient';
import { useGiveawayConfigQuery } from '@modules/Dashboard/pages/Apps/pages/AppsTwitch';
import { useMutation, useQuery } from '@tanstack/react-query';
import { routing } from '@utils/routing';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import kebabCase from 'lodash-es/kebabCase';
import { Dashboard } from '@components/common/Dashboard';
import { Onboarding } from '@components/common/Onboarding';
import { PageLayout } from '@components/common/PageLayout';
import { Suggestion } from '@components/common/Suggestion';
import { useHelpAndSupportRecommendations } from '@modules/Dashboard/Home/modules/recommendations/useHelpAndSupportRecommendations';
import { useRecommendations } from '@modules/Dashboard/Home/modules/recommendations/useRecommendations';
import { useDashboardExperiments } from './hooks/useDashboardExperiments';
import { SectionTitle } from './components/SectionTitle';
import { useActionsBars } from './hooks/useActionsBars';
import { useCurrentShopQuery } from '../hooks/useCurrentShopQuery';
import { NextStepsBanner } from './components/NextStepsBanner';
import { useBlogPosts } from './hooks/useBlogPosts';
import { useHome } from './hooks/useHome';
import { useHomeExperiments } from './hooks/useHomeExperiments/useHomeExperiments';
import { ActiveVisitors } from './modules/ActiveVisitors';
import { HomeAnalyticsContainer } from './modules/HomeAnalytics';
import * as S from './styled';
import { ArticleListSection } from './components/ArticleListSection';
import { Scheduler } from './components/Scheduler';
export const Home = () => {
    const { home } = useHome();
    const { onboarding } = home;
    const { hasPermission } = useDashboardPermissions();
    const { currentShopQuery } = useCurrentShopQuery();
    const { recommendations } = useRecommendations();
    const { helpRecommendations } = useHelpAndSupportRecommendations();
    const { recommendationsFiltered, helpRecommendationsFiltered } = useHomeExperiments(recommendations, helpRecommendations);
    const { actionsBars } = useActionsBars();
    const { blogPosts } = useBlogPosts();
    const location = useLocation();
    const { giveawayConfigQuery } = useGiveawayConfigQuery();
    const [showAllMarketingCampaigns] = useQueryParam('showAllMarketingCampaigns', StringParam);
    const activeCampaignQuery = useQuery({
        queryKey: ['activeCampaign'],
        queryFn: async () => {
            const { data } = await InternalApiClient.getActiveCampaign({
                showAllMarketingCampaigns: showAllMarketingCampaigns || undefined,
            });
            return data;
        },
    });
    const enrollMutation = useMutation({
        mutationFn: async (campaignId) => {
            await InternalApiClient.enrollCampaign({ campaignId });
        },
        onSuccess: () => {
            Toasts.notify('You have successfully enrolled in the campaign.', { type: 'success' });
        },
    });
    const renderTwitchNextStepsBanner = () => {
        if (!currentShopQuery.isSuccess || !giveawayConfigQuery.isSuccess)
            return null;
        const { metafields, social } = currentShopQuery.data;
        const hasTwitchPlatform = 
        // @ts-ignore
        metafields?.onboarding?.['oauth-flow'] === true &&
            // @ts-ignore
            metafields?.onboarding?.['creator-platform'] === 'twitch';
        const hasTwitchSocial = !!social.twitch;
        if (giveawayConfigQuery.data.enabled)
            return null;
        if (hasTwitchPlatform || hasTwitchSocial) {
            return (React.createElement(NextStepsBanner, { title: "Boost Sales with Twitch Gifting \uD83C\uDF81", description: "Grow your sales by 50% by letting supporters gift products to chat.", buttons: [
                    {
                        label: 'Set up now',
                        to: routing.apps.twitch,
                        state: { from: { label: SECTION_NAMES.home, location } },
                    },
                ], color: "#9146ff" }));
        }
    };
    const { disableOnboardingMarketingCampaign } = useDashboardExperiments();
    const renderBrandNextStepsBanner = () => {
        if (!activeCampaignQuery.isSuccess)
            return null;
        if (activeCampaignQuery.data === null)
            return null;
        if (disableOnboardingMarketingCampaign)
            return null;
        const { displayInfo, onboardingCampaignId } = activeCampaignQuery.data;
        if (!displayInfo || !onboardingCampaignId)
            return null;
        const mapButton = (button) => {
            if (button.type === 'Enroll') {
                return {
                    label: button.title,
                    disabled: enrollMutation.isLoading,
                    onClick: async () => {
                        await enrollMutation.mutateAsync(onboardingCampaignId);
                        activeCampaignQuery.refetch();
                    },
                };
            }
            return { label: button.title, href: button.uri.toString() };
        };
        const buttons = [];
        if (displayInfo.primaryButton) {
            buttons.push(mapButton(displayInfo.primaryButton));
        }
        if (displayInfo.secondaryButton) {
            buttons.push(mapButton(displayInfo.secondaryButton));
        }
        return (React.createElement(NextStepsBanner, { title: displayInfo.title, description: displayInfo.description, buttons: buttons, color: "#e32066" }));
    };
    const content = (['ONBOARDING_MEMBERSHIPS', 'NONE'].includes(onboarding.onboardingType) ||
        !!currentShopQuery.data?.lastLiveAt) &&
        hasPermission('home.analytics') && (React.createElement(S.Content, null,
        React.createElement(ActiveVisitors, null),
        React.createElement(HomeAnalyticsContainer, null)));
    return (React.createElement(PageLayout, null,
        React.createElement(Dashboard.Section, { name: home.name, title: home.title },
            content,
            onboarding.schedulerProps && React.createElement(Scheduler, { ...onboarding.schedulerProps }),
            onboarding.onboardingProps && (React.createElement("div", { style: { marginTop: 48 } },
                React.createElement(Onboarding, { ...onboarding.onboardingProps }))),
            React.createElement("div", { style: { marginTop: 48 } },
                React.createElement(SectionTitle, { title: "Next steps" }),
                React.createElement(S.ActionBarsContainer, null,
                    hasPermission('home.recommendations') && (React.createElement(React.Fragment, null,
                        renderBrandNextStepsBanner(),
                        renderTwitchNextStepsBanner())),
                    ['ONBOARDING_MEMBERSHIPS', 'NONE'].includes(onboarding.onboardingType) &&
                        !!actionsBars.length && (React.createElement(RecommendationButtonGroup, null, actionsBars.map((actionBar, index) => (React.createElement(RecommendationButton, { key: `actionBar-${index}`, ...actionBar })))))),
                recommendationsFiltered.length > 0 && (React.createElement(S.Recommendations, null, recommendationsFiltered.map(({ suggestionProps, id }) => {
                    if (suggestionProps.type === 'node') {
                        return (React.createElement(S.SuggestionWrapper, { key: `recommendation-${id}`, "data-analytics-section": id }, suggestionProps.node));
                    }
                    return (React.createElement(S.SuggestionWrapper, { "data-analytics-section": id, key: kebabCase(suggestionProps.props.title) },
                        React.createElement(Suggestion, { ...suggestionProps.props })));
                })))),
            helpRecommendationsFiltered.length > 0 && (React.createElement("div", { style: { marginTop: 48 } },
                React.createElement(SectionTitle, { title: "Help & support" }),
                React.createElement(S.Recommendations, null, helpRecommendationsFiltered.map(({ suggestionProps, id }) => {
                    if (suggestionProps.type === 'node') {
                        return (React.createElement(S.SuggestionWrapper, { key: `help-recommendation-${id}`, "data-analytics-section": id }, suggestionProps.node));
                    }
                    return (React.createElement(S.SuggestionWrapper, { "data-analytics-section": id, key: kebabCase(suggestionProps.props.title) },
                        React.createElement(Suggestion, { ...suggestionProps.props })));
                })))),
            blogPosts && React.createElement(ArticleListSection, { ...blogPosts }))));
};
